import { type ReactElement, type ReactNode, Suspense, useCallback, useContext, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { isNil } from "lodash-es";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import {
  MultiVariateFeature,
  RecordParticipationTypes,
  useFeatureTernary,
  useSimpleFeatureTernary,
} from "@viagogo/feature";
import { RecentlyViewedDateRange } from "src/http/types";
import type {
  ComingUpForYouItem,
  ConnectToSpotifyModel,
  EventListGridItem,
  HomePageViewModel,
  PaginatedQueryOfIEventGridItem,
  RecentlyViewedModel,
} from "src/http/types";
import { Content, useContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PAGE_IDS } from "src/shared/constants/PageIds";
import { TopLevelCategory } from "src/shared/constants/TopLevelCategories";
import { useAppContext } from "src/shared/context/AppContext";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { useIsMobileClientApp, useIsStubhub, useIsUKUser } from "src/shared/utils/appContextHelper";
import { lazyWithRetry } from "src/shared/utils/lazyWithRetry";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import { STALE_TIME_ONE_HOUR } from "src/shared/utils/query/retry-constants";
import { DateType } from "src/types/shared/cookies";
import type { Location } from "src/types/shared/cookies";
import { UpcomingEventsBasedOnYourFavoritesHomeSection } from "src/website-apps/responsive/stubhub-home/components/grid/favorites-fs/UpcomingEventsBasedOnYourFavoritesHomeSection";
import { HOME_CAROUSEL_KEYS } from "src/website-apps/responsive/stubhub-home/components/HomeLayoutBody/constants";
import {
  isMatchupEvent,
  toRequestBasePath,
} from "src/website-apps/responsive/stubhub-home/components/HomeLayoutBody/favorite-team-carousel/utils";
import { RecommendedForYouOnHome } from "src/website-apps/responsive/stubhub-home/components/HomeLayoutBody/RecommendedForYouOnHome";
import { SavedEventsOnHome } from "src/website-apps/responsive/stubhub-home/components/HomeLayoutBody/SavedEventsOnHome";
import { ViewportWrapper } from "src/website-apps/responsive/stubhub-home/components/LazyCarouselSection";
import { CarouselSkeleton } from "src/website-apps/responsive/stubhub-home/components/LazyCarouselSection/CarouselSkeleton";
import {
  MoreFromGenresYouLike,
  useMoreFromGenresYouLikeQuery,
} from "src/website-apps/responsive/stubhub-home/components/more-from-genres-you-like/MoreFromGenresYouLike";
import { useAddRecentlyViewedToModel } from "src/website-apps/responsive/stubhub-home/hooks/useAddRecentlyViewedToModel";
import { useDedupedCarousels } from "src/website-apps/responsive/stubhub-home/hooks/useDedupedCarousels";
import { useSavedEventsFavoritesSync } from "src/website-apps/responsive/stubhub-home/hooks/useSavedEventsFavoritesSync";
import {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { CarouselSize } from "src/website-apps/shared/components/CarouselSize";
import { GenericCategoryGridWrapper } from "src/website-apps/shared/components/EitherTlcGridsOrCarousels/GenericCategoryGridWrapper";
import { GenericEventCarousel } from "src/website-apps/shared/components/GenericEventCarousel";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { useRecommendationsExperimentPageEvent } from "src/website-apps/shared/components/home/useRecommendationsExperimentPageEvent";
import { InViewTracker } from "src/website-apps/shared/components/InViewTracker";
import { PromoBannerSkeleton } from "src/website-apps/shared/components/promo-banner/PromoBannerSkeleton";
import { HOME_CAROUSEL_IDS } from "src/website-apps/shared/components/responsive-carousel/constants";
import { renderWithResponsiveCarousel } from "src/website-apps/shared/components/responsive-carousel/util";
import { HomeSeasonTicketsAppPromo } from "src/website-apps/shared/components/season-tickets-app-promo";
import { HOME_EVENT_NAMES } from "src/website-apps/shared/constants/EventNames";
import { ContentLayoutContainer } from "src/website-apps/shared/context/ContentLayoutContext";
import { DateContext, useDateCookieOptions, useDatesContext } from "src/website-apps/shared/context/date-context";
import { useCarouselDimensions } from "src/website-apps/shared/hooks/useCarouselDimensions";
import { useStableNow } from "src/website-apps/shared/hooks/useStableNow";
import { SEARCH_DISCOVERY_ENTITY_TYPES } from "src/website-apps/shared/utils/searchPageEvents";
import { SapphireRewardsBanner } from "../chase-rewards/SapphireRewardsBanner";
import { ConnectToSpotifyBanner } from "../connect-to-spotify/ConnectToSpotifyBanner";
import { BannersContainer } from "./banners/BannersContainer";
import { BecauseYouBrowsedOnHome } from "./BecauseYouBrowsedOnHome";
import { ComingUpForYouOnHome } from "./ComingUpForYouOnHome";
import { DontMissEvents } from "./dont-miss-carousels";
import { EmailOptInBanner } from "./email-opt-in-banner";
import { FavoriteTeamCarousel } from "./favorite-team-carousel/FavoriteTeamCarousel";
import { FilterSection } from "./FilterSection";
import { HeroBanner } from "./HeroBanner";
import { RecentlyViewedOnHome } from "./RecentlyViewedOnHome";
import { SpotifyCarouselOnHome } from "./SpotifyCarouselOnHome";
import { TopSubcategoriesOnHome } from "./TopSubcategoriesOnHome";
import { TrendingEventsInLocaleOnHome } from "./TrendingEventsInLocaleOnHome";
import { useCarouselSorting } from "./use-carousel-sorting";
import { useEmailOptInStatus } from "./useEmailOptInStatus";
import { WorldCupWidget } from "./world-cup-widget/WorldCupWidget";

const LazyPromoBanner = lazyWithRetry(() =>
  import("src/website-apps/shared/components/promo-banner/PromoBanner").then((module) => ({
    default: module.PromoBanner,
  })),
);

export interface CarouselToRender {
  carouselId: HOME_CAROUSEL_IDS;
  kind: number;
  shouldRender: boolean;
  render: () => ReactElement;
}

// Stable reference so pre-fetch renders don't hand the carousel a fresh array each pass.
const EMPTY_COMING_UP_FOR_YOU_ITEMS: ComingUpForYouItem[] = [];

interface FavoriteTeamDateRange {
  from?: string | null | undefined;
  to?: string | null | undefined;
}

const DAY_IN_MS = 24 * 60 * 60 * 1000;

// Mirrors the GetFavoriteTeam window, rounded up to a UTC day boundary so the query key stays stable across renders.
const capToLookahead = (
  to: string | null | undefined,
  maxLookaheadDays: number | null | undefined,
  nowMillis: number,
) => {
  if (!maxLookaheadDays) {
    return to;
  }
  const lookaheadEnd = new Date(Math.ceil((nowMillis + maxLookaheadDays * DAY_IN_MS) / DAY_IN_MS) * DAY_IN_MS);
  return to && new Date(to) < lookaheadEnd ? to : lookaheadEnd.toJSON();
};

const useFavoriteTeamCarouselData = (enabled: boolean, dateRange: FavoriteTeamDateRange | undefined) => {
  const createQueryFn = useCreateQueryFn();
  const { data: team, isLoading: isTeamLoading } = useQuery<{
    categoryId: number;
    url: string | null;
    maxLookaheadDays?: number | null;
  } | null>({
    queryKey: createQueryKey(QueryKeyPrefix.FAVORITE_TEAM_HOME),
    queryFn: createQueryFn({
      method: "GetFavoriteTeam",
      basePath: "/favorites",
      params: [],
    }),
    staleTime: STALE_TIME_ONE_HOUR,
    enabled,
  });
  const teamCategoryId = team?.categoryId;
  const nowMillis = useStableNow().valueOf();
  const toDate = capToLookahead(dateRange?.to, team?.maxLookaheadDays, nowMillis);
  const { data: events, isLoading: isEventsLoading } = useQuery<{ items?: EventListGridItem[] }>({
    queryKey: createQueryKey(QueryKeyPrefix.FAVORITE_TEAM_EVENTS_HOME, {
      categoryId: teamCategoryId,
      from: dateRange?.from,
      to: toDate,
    }),
    queryFn: createQueryFn({
      method: "GetTeamMatches",
      params: [
        ["categoryId", teamCategoryId as number],
        ["maxRows", 10],
        ["includeFromPrice", false],
        ...(dateRange?.from ? [["fromDate", dateRange.from] as [string, string]] : []),
        ...(toDate ? [["toDate", toDate] as [string, string]] : []),
      ],
      basePath: toRequestBasePath(team?.url),
    }),
    staleTime: STALE_TIME_ONE_HOUR,
    enabled: enabled && teamCategoryId !== undefined && !!team?.url,
  });
  const matchupEvents = (events?.items ?? []).filter((event) => isMatchupEvent(event.eventMetadata));
  return {
    categoryId: teamCategoryId,
    url: team?.url,
    events: matchupEvents,
    isLoading: enabled && (isTeamLoading || isEventsLoading),
    hasUpcomingEvents: teamCategoryId !== undefined && matchupEvents.length > 0,
  };
};

export interface UseCarouselComponentsParams {
  model: HomePageViewModel;
  selectedCarouselGenre: number;
  setSelectedCarouselGenre(genre: number): void;
  /**
   * When true, the email opt-in slot swaps the banner for the popup trigger (exp 103701, popup
   * arm with its gate met). The popup then appears when the slot scrolls into view.
   */
  swapEmailOptInBannerForPopup?: boolean;
  /**
   * Fired once when the email opt-in slot scrolls into view — the arm-agnostic participation
   * trigger for exp 103701. Supplied by the parent only for the eligible population
   * (anonymous, consenting, desktop), so the in-view tracker mounts only when the user can
   * actually participate.
   */
  onEmailOptInSlotInView?: (() => void) | undefined;
  /**
   * Current user location, injected by the parent (which reads LocationContext) so this hook
   * stays context-free and unit-testable. Drives the NGS home banner's football-proximity
   * refetch on location change (FS-1065).
   */
  userLocation?: Location | null;
}

export interface UseCarouselComponentsResult {
  sortedComponents: CarouselToRender[];
}

export const useCarouselComponents = (params: UseCarouselComponentsParams): UseCarouselComponentsResult => {
  "use no memo";
  const createQueryFn = useCreateQueryFn();
  const {
    model,
    selectedCarouselGenre,
    setSelectedCarouselGenre,
    swapEmailOptInBannerForPopup = false,
    onEmailOptInSlotInView,
    userLocation,
  } = params;

  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const isStubhub = useIsStubhub();
  const appContext = useAppContext();

  // NGS "Connect your season tickets in the app" home banner (FS-1065): desktop only (excludes
  // tablet), when the user's location has a nearby NFL/NCAAF team venue, and only on the All /
  // Sports tabs. isDesktop feeds isNgsSeasonTicketsBannerEligible below (the canParticipate for
  // useSimpleFeatureTernary), so true tablets — served by the same Tablet Web controller — do not
  // record experiment participation.
  // The proximity signal is re-fetched on location change — the SSR model.hasNearbyFootballTeam
  // only reflects the location at initial page load — with the SSR value seeding the placeholder
  // to avoid a flash. Desktop-only endpoint lives on Home_TabletController.
  const { lt, lg } = userLocation ?? {};
  const { data: hasNearbyFootballTeam = false } = useQuery({
    queryKey: createQueryKey(QueryKeyPrefix.HAS_NEARBY_FOOTBALL_TEAM, { lt, lg }),
    queryFn: createQueryFn<boolean>({ method: "GetHasNearbyFootballTeam", params: [] }),
    placeholderData: model.hasNearbyFootballTeam ?? false,
    staleTime: Infinity,
    enabled: isStubhub && isDesktop,
  });
  const isNgsSeasonTicketsBannerEligible =
    isStubhub &&
    isDesktop &&
    hasNearbyFootballTeam &&
    (selectedCarouselGenre === TopLevelCategory.ALL || selectedCarouselGenre === TopLevelCategory.SPORT);
  const showNgsSeasonTicketsBanner =
    useSimpleFeatureTernary(
      FeatureSetEnum.TNgsMarketingForNflNcaaf,
      false,
      true,
      () => isNgsSeasonTicketsBannerEligible,
    ) ?? false;
  const isUKUser = useIsUKUser();

  // Internalized hooks - called inside this hook to reduce parent coupling
  const concertsText = useContent("cid$#1081941#");
  const sportsText = useContent("cid$#1081942#");
  const theatreText = useContent("cid$#1081943#");
  const comedyText = useContent("cid$#3234659#");
  const { recommendationsPageEvent } = useRecommendationsExperimentPageEvent();
  const dateCookieOptions = useDateCookieOptions();
  const [dates] = useDatesContext(false, dateCookieOptions);
  const { isUserLoggedIn, isNativeApp } = appContext;

  const { from: dateFrom, to: dateTo } = dates?.dates ?? {};
  const spotifyFilterKey = `${dateFrom instanceof Date ? dateFrom.toJSON() : (dateFrom ?? "")}-${dateTo instanceof Date ? dateTo.toJSON() : (dateTo ?? "")}-${lt ?? ""}-${lg ?? ""}`;
  const initialSpotifyFilterKeyRef = useRef(spotifyFilterKey);
  const hasSpotifyFilterChanged = spotifyFilterKey !== initialSpotifyFilterKeyRef.current;

  // "Promo banner" (CRH-761, 2a): US-location users only (the user's selected location, per Megan —
  // not affiliate or geo-IP), nationwide within the US, no DMA pre-filter. Eligibility also waits for
  // the Spotify data to settle (see isPromoBannerEligible below) so connected users never see it.

  const carouselSize = CarouselSize.REGULAR;

  // RV data source (exp 103068): control carries the model in SSR; variant fetches it here at
  // the registry level (like Coming up for you below) so the position logic stays data-driven in
  // BOTH arms — the moveUp promotion and RFY dedupe need dateRange/items before the component
  // mounts, and a viewport-gated fetch inside RecentlyViewedOnHome can't provide that (the
  // carousel would silently lose its promoted top slot in the variant). A null model field is
  // the server's authoritative variant signal. The query key is shared with the component's
  // own lazy query, so React Query dedupes to a single request.
  const { data: fetchedRecentlyViewed } = useQuery<RecentlyViewedModel>({
    queryKey: createQueryKey(QueryKeyPrefix.RECENTLY_VIEWED_HOME),
    queryFn: createQueryFn({ method: "GetRecentlyViewedItems", params: [] }),
    staleTime: Infinity,
    enabled: isNil(model.recentlyViewedItems),
  });
  const recentlyViewedModel = model.recentlyViewedItems ?? fetchedRecentlyViewed ?? null;
  const { recentlyViewedItems, showAddRecentlyViewedToModel, recentlyViewedTimeWindow } = useAddRecentlyViewedToModel({
    ...model,
    recentlyViewedItems: recentlyViewedModel,
  });

  const moveUpRecentlyViewedCarousel =
    recentlyViewedTimeWindow === RecentlyViewedDateRange.ThreeDays ||
    recentlyViewedTimeWindow === RecentlyViewedDateRange.SevenDays ||
    recentlyViewedTimeWindow === RecentlyViewedDateRange.FourteenDays;

  const showRecommendedEvents = (model.recommendationEvents.items?.length ?? 0) > 0;
  const showComedy = (model.comedy.categoryItems?.items?.length ?? 0) > 0;

  const dedupeCategoryIds = useDedupedCarousels({
    model,
    showComedy,
    recentlyViewedItems,
    showAddRecentlyViewedToModel,
    showRecommendedForYou: true,
  });

  // TM-Home-FixYourSavedEvents (103843): restores the carousel broken by progressive loading (101864).
  // Fetch at registry level so shouldRender is data-driven — avoids entityPosition shift.
  const showSavedEvents =
    useSimpleFeatureTernary(FeatureSetEnum.TMHomeFixYourSavedEvents, false, true, () => !isNativeApp) ?? false;
  const hasSavedEventsModelData = (model.savedEvents?.items?.length ?? 0) > 0;
  const { data: fetchedSavedEvents, isError: savedEventsError } = useQuery<PaginatedQueryOfIEventGridItem>({
    queryKey: createQueryKey(QueryKeyPrefix.SAVED_EVENTS_HOME, { subscriptionType: ENTITY_TYPES.EVENT }),
    queryFn: createQueryFn({
      method: "UserSubscriptions",
      basePath: "/favorites",
      params: [
        ["subscriptionType", ENTITY_TYPES.EVENT],
        ["maxRows", 12],
        ["page", 0],
      ],
    }),
    enabled: showSavedEvents,
    // Seed the query with the server model when it is present, so the carousel reads from a single source (the
    // query cache) on both the SSR-model and client-fetch paths. Without this, the SSR-model path keeps serving
    // the immutable model and the favorites sync below never applies.
    ...(hasSavedEventsModelData && { initialData: model.savedEvents }),
    staleTime: STALE_TIME_ONE_HOUR,
  });
  const savedEventsData = fetchedSavedEvents;
  useSavedEventsFavoritesSync(showSavedEvents);
  // Content-sensitive so the grid re-renders when the settle refetch returns the same event ids with fuller
  // item data (the optimistic insert omits server-computed fields such as price).
  const savedEventsSyncKey = JSON.stringify(savedEventsData?.items ?? []);
  const isFetchingSavedEvents =
    showSavedEvents && !hasSavedEventsModelData && fetchedSavedEvents === undefined && !savedEventsError;
  const showSavedEventsCarousel =
    showSavedEvents && (isFetchingSavedEvents || (savedEventsData?.items?.length ?? 0) > 0);

  const carouselDim = useCarouselDimensions(carouselSize);
  const isBaseCardRefactor =
    useSimpleFeatureTernary(FeatureSetEnum.UseGridCardBaseComponentRefactor, false, true) ?? false;

  const responsiveCarouselRenderFunction = useCallback(
    ({
      carouselId,
      analyticsCarouselId,
      analyticsItemType,
    }: {
      carouselId: number;
      analyticsCarouselId?: CarouselId | undefined;
      analyticsItemType?: CarouselItemType | undefined;
    }) =>
      renderWithResponsiveCarousel({
        size: carouselDim.carouselSize,
        hidePaginationButtons: isMobile,
        carouselId,
        analyticsCarouselId,
        analyticsItemType,
        gapPx: carouselDim.gapPx,
        useFlex: isBaseCardRefactor,
      }),
    [carouselDim, isMobile, isBaseCardRefactor],
  );

  // Lazy-load the email opt-in subscription status independently of the progressive carousel
  // loading experiment (101864). This fixes the bug where emailOptInBannerModel was null
  // for users in that experiment variant because the server-side subscription check was
  // in an early-return path gated by the now-deployed experiment 25263.
  const { shouldShowEmailOptInBanner: shouldShowFromStatus, email: emailOptInEmail } = useEmailOptInStatus(
    isUserLoggedIn ?? false,
  );

  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const shouldShowEmailOptInBanner = shouldShowFromStatus && !isBannerDismissed;

  const handleEmailOptIn = useCallback(() => {
    setIsBannerDismissed(true);
  }, []);

  // Popup-arm slots render the in-view trigger instead of the banner. Branching on the callback
  // (rather than swapEmailOptInBannerForPopup alone) narrows it for the InViewTracker prop.
  const emailOptInPopupTrigger = swapEmailOptInBannerForPopup ? onEmailOptInSlotInView : undefined;

  // Visibility flags
  const upcomingEventsHasData = !!model.recommendedBasedOnFavoriteEvents?.items?.length;
  const showTrendingEvents = !isUKUser;
  // Exp 103068: recentlyViewedModel is null only while the variant's registry-level fetch is
  // in flight (control always carries an SSR model, possibly with an empty output) — keep the
  // slot during that window so the carousel doesn't pop in from nowhere. Once data exists in
  // either arm, the slot only renders when there are items, matching control's pre-experiment
  // behavior (an empty invisible slot would shift entityPosition for carousels below).
  const showRecentlyViewed = isNil(recentlyViewedModel) || !!recentlyViewedItems?.items?.length;

  const isInRemovePopularCategoriesExperiment = useSimpleFeatureTernary(
    FeatureSetEnum.CXWD_3151_RemovePopularCategoriesCarouselFromHomepage,
    false,
    true,
  );

  const isFilterFocusMode = useSimpleFeatureTernary(
    FeatureSetEnum.CXWD_HomeFilterFocusMode,
    false,
    true,
    () => selectedCarouselGenre !== 0,
  );

  // "Coming up for you" data source (DMS-225, exp 103527): the SSR model carries items for control
  // of exp 103068; the variant fetches them here at the registry level (not inside the component)
  // so the 103527 `canParticipate` gate below stays data-driven in BOTH arms. A null model field is
  // the server's authoritative exp-103068-variant signal — control always serializes a (possibly
  // empty) list, so this query never fires for control users.
  const { data: fetchedComingUpForYou } = useQuery<ComingUpForYouItem[]>({
    queryKey: createQueryKey(QueryKeyPrefix.COMING_UP_FOR_YOU_HOME),
    queryFn: createQueryFn({ method: "GetComingUpForYouItems", params: [] }),
    staleTime: Infinity,
    enabled: (isUserLoggedIn ?? false) && isNil(model.comingUpForYouItems),
  });
  const comingUpForYouItems = model.comingUpForYouItems ?? fetchedComingUpForYou;

  // Exp 103527 participation is recorded here, only for users with eligible items (pristine
  // participation) — this is also the recording point for the variant-2 SeeAgain toast arm,
  // whose toast wiring is server-side. 0 = control (hidden) · 1 = carousel only ·
  // 2 = carousel + purchase-based SeeAgain toast.
  const comingUpForYouTernary = useFeatureTernary(
    FeatureSetEnum.WebHomeComingUpForYouCarousel,
    () => (comingUpForYouItems?.length ?? 0) > 0,
  );
  const comingUpForYouVariant = comingUpForYouTernary({ control: () => 0, variant: (_data, variant) => variant }) ?? 0;

  const showWorldCupHomepageWidget =
    useSimpleFeatureTernary(FeatureSetEnum.CXWD_WorldCupHomepageWidget, false, true) ?? false;

  // Favorite team carousel (exp 103970): while the experiment is effective (server-side, arm-independent,
  // unrecorded), the team and its events are fetched for every signed-in user on the tabs that show the
  // carousel, in both arms, so participation can depend on the data. Only users who
  // would actually see it (signed in, on one of those tabs, with a team that has matchups in the selected
  // date range) are recorded; shouldRender stays data-driven so no one else occupies a slot.
  const isFavoriteTeamEligibleUser =
    isUserLoggedIn &&
    (selectedCarouselGenre === TopLevelCategory.ALL || selectedCarouselGenre === TopLevelCategory.SPORT);
  // Read the live DateContext (like the other home carousels): `useDatesContext` above only snapshots the cookie at mount.
  const liveDates = useContext(DateContext)?.[0];
  const { from: liveDateFrom, to: liveDateTo } = liveDates?.dates ?? {};
  const hasUserDateFilter =
    liveDates?.type !== undefined && liveDates.type !== DateType.ALL_DATES && !!(liveDateFrom || liveDateTo);
  const favoriteTeamDateRange: FavoriteTeamDateRange | undefined = hasUserDateFilter
    ? {
        from: liveDateFrom instanceof Date ? liveDateFrom.toJSON() : liveDateFrom,
        to: liveDateTo instanceof Date ? liveDateTo.toJSON() : liveDateTo,
      }
    : undefined;
  const favoriteTeam = useFavoriteTeamCarouselData(
    isFavoriteTeamEligibleUser && (model.isFavoriteTeamCarouselEffective ?? false),
    favoriteTeamDateRange,
  );
  const showFavoriteTeamCarousel =
    useSimpleFeatureTernary(
      FeatureSetEnum.TMHomeFavoriteTeamCarousel,
      false,
      true,
      () => isFavoriteTeamEligibleUser && favoriteTeam.hasUpcomingEvents,
    ) ?? false;
  // Raw assignment without recording participation, so only variant users get the loading skeleton.
  const isAssignedFavoriteTeamVariant =
    useSimpleFeatureTernary(FeatureSetEnum.TMHomeFavoriteTeamCarousel, false, true, undefined, false) ?? false;

  // "More from Genres You Like" marquee (exp 102911, discovery-web-mb-music-you-like).
  // Gated on the flag alone: the collection lazy-fetches its events (the genre seed — recently
  // viewed events + categories — is not on the SSR model), and the component self-hides when there
  // is no genre signal. Refining `canParticipate` to a server-provided seed-presence flag, so only
  // users who actually get the collection enter the split, is a follow-up.
  const showMbMusicYouLike = useSimpleFeatureTernary(FeatureSetEnum.DiscoveryWebMbMusicYouLike, false, true) ?? false;

  const showBecauseYouBrowsed = useSimpleFeatureTernary(FeatureSetEnum.TMHomeBecauseYouBrowsed, false, true) ?? false;

  // "More from Genres You Like" relaunch (exp 103106, discovery-web-mb-music-you-like-v2): same collection
  // re-rendered as a standard home carousel instead of the marquee, with a variant-driven title.
  // 0 = control (hidden) · 1 = plain "More from Genres You Like" title ·
  // 2 = grounded in the user's top 2 genres, titled "Because you like <A> & <B>".
  // Runs on its own experiment id so this can merge while 102911 is still live; 102911 is being
  // stopped before this one starts, and the relaunch arms take precedence below if both are ever active.
  const mbMusicYouLikeV2 = useFeatureTernary(FeatureSetEnum.DiscoveryWebMbMusicYouLikeV2);
  const mbMusicYouLikeV2Variant = mbMusicYouLikeV2({ control: () => 0, variant: (_data, variant) => variant }) ?? 0;

  // Music genres → concert events only, so the slot is limited to the All/Concerts filters
  // (mirrors the Concerts slot). The data is fetched here at the registry level — not inside
  // the component — so the slot can be excluded until items are confirmed: a slot rendering
  // null still consumes a visibleIndex/CarouselProvider, which would shift entityPosition for
  // every carousel below it while the fetch is pending and for users with no genre signal,
  // corrupting cross-arm telemetry (same reasoning as the Coming up for you slot above).
  const showMbMusicYouLikeSlot =
    (mbMusicYouLikeV2Variant > 0 || showMbMusicYouLike) &&
    (selectedCarouselGenre === TopLevelCategory.CONCERT || selectedCarouselGenre === TopLevelCategory.ALL);
  const { data: mbMusicYouLikeData } = useMoreFromGenresYouLikeQuery({
    maxGenres: mbMusicYouLikeV2Variant >= 2 ? 2 : undefined,
    enabled: showMbMusicYouLikeSlot,
  });

  // Progressive carousel loading experiment: delay non-hero carousels until they enter the viewport.
  // The server sets this flag directly (experiment 101864 is excluded from client-side experiment data).
  const isProgressiveCarouselLoading = model.isProgressiveCarouselLoading ?? false;

  // MHomeReduceEventCardSize (23424): deployed variant targeted mobile client apps only.
  const reduceEventCardSizes = useIsMobileClientApp();
  const useImprovedSkeleton = useSimpleFeatureTernary(FeatureSetEnum.DiscoveryImprovedLoadingSkeleton, false, true);

  const wrapCarousel = (content: () => ReactElement, skeleton: ReactNode = <CarouselSkeleton />) =>
    isProgressiveCarouselLoading ? <ViewportWrapper renderContent={content} skeleton={skeleton} /> : content();

  const eventSkeleton =
    useImprovedSkeleton && reduceEventCardSizes ? <CarouselSkeleton variant="medium4x3" /> : undefined;

  // Control (exp 103068): connectToSpotify is in the SSR model — always non-null, so the
  // lazy query never fires. Variant: the server leaves it null and the client fetches on
  // mount — the fetch must stay at this level (not viewport-gated) because the Spotify
  // banner/carousel visibility flags below are needed for the initial carousel registry.
  const { data: fetchedSpotify } = useQuery<ConnectToSpotifyModel>({
    queryKey: createQueryKey(QueryKeyPrefix.CONNECT_TO_SPOTIFY_HOME, { from: dateFrom, to: dateTo, lt, lg }),
    queryFn: createQueryFn({
      method: "GetSpotifyData",
      params: [
        ...(lt !== null && lt !== undefined ? [["lt", lt] as [string, number]] : []),
        ...(lg !== null && lg !== undefined ? [["lg", lg] as [string, number]] : []),
        ...(dateFrom
          ? [["fromDate", dateFrom instanceof Date ? dateFrom.toJSON() : dateFrom] as [string, string]]
          : []),
        ...(dateTo ? [["toDate", dateTo instanceof Date ? dateTo.toJSON() : dateTo] as [string, string]] : []),
      ],
    }),
    staleTime: Infinity,
    enabled: !model.connectToSpotify || hasSpotifyFilterChanged,
  });
  const connectToSpotify = fetchedSpotify ?? model.connectToSpotify;

  const hasSpotifyConnectUrl = !!connectToSpotify?.connectUrl;
  const isSpotifyConnected = !!connectToSpotify?.isSpotifyConnected;
  // Fail closed: only treat Spotify state as known once the SSR model or the client fetch has
  // actually produced a value. Falling back to `!isPending` here would let a failed fetch look
  // settled and flash the promo banner to Spotify-connected users.
  const hasSpotifyState = connectToSpotify !== null && connectToSpotify !== undefined;
  const isPromoBannerUsLocation = userLocation?.ct === "US";
  const isPromoBannerPending = isStubhub && !isNativeApp && isPromoBannerUsLocation && !hasSpotifyState;
  const isPromoBannerEligible =
    isStubhub && !isNativeApp && isPromoBannerUsLocation && hasSpotifyState && !isSpotifyConnected;
  const hasChaseSapphireBanner = !!model.sapphireRewardsBanner?.learnMoreUrl;
  const isNonMusicFilter =
    selectedCarouselGenre !== TopLevelCategory.ALL && selectedCarouselGenre !== TopLevelCategory.CONCERT;

  const hasSpotifyBanner =
    useSimpleFeatureTernary(
      FeatureSetEnum.TMGlobalSpotifyBannerWithStatus,
      hasSpotifyConnectUrl,
      hasSpotifyConnectUrl || isSpotifyConnected,
    ) ?? false;

  const showSpotifyConnectedState = useSimpleFeatureTernary(
    FeatureSetEnum.TMGlobalSpotifyBannerWithStatus,
    false,
    isSpotifyConnected,
  );

  const hideSpotifyOnFilter =
    useSimpleFeatureTernary(
      FeatureSetEnum.CXWD_HomeFilterHideSpotifyBanner,
      false,
      true,
      () => hasSpotifyBanner && isNonMusicFilter,
    ) ?? false;

  const showRecentlyViewedAboveHeroBanner =
    useSimpleFeatureTernary(FeatureSetEnum.CXWD_RecentlyViewedAboveHeroBanner, false, true) ?? false;

  const showSpotifyBanner = hasSpotifyBanner && !hideSpotifyOnFilter;
  const hasSpotifyData =
    (connectToSpotify?.options?.items?.length ?? 0) > 0 ||
    (connectToSpotify?.eventsFromSpotifyFavorites?.items?.length ?? 0) > 0 ||
    isSpotifyConnected;
  const spotifyOptions = connectToSpotify?.options;
  const spotifyConnectUrl = connectToSpotify?.connectUrl;
  // Carousel definitions
  const carousels: CarouselToRender[] = [
    {
      carouselId: HOME_CAROUSEL_IDS.heroBanner,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
      shouldRender: true,
      render: () => <HeroBanner initialData={model.heroBannerContent} selectedCarouselGenre={selectedCarouselGenre} />,
    },
    {
      carouselId: HOME_CAROUSEL_IDS.filters,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.EVENT,
      shouldRender: true,
      render: () => (
        <FilterSection
          selectedCarouselGenre={selectedCarouselGenre}
          setSelectedCarouselGenre={setSelectedCarouselGenre}
        />
      ),
    },
    {
      carouselId: HOME_CAROUSEL_IDS.spotifyYourMusicCarousel,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
      shouldRender:
        (selectedCarouselGenre === TopLevelCategory.ALL || selectedCarouselGenre === TopLevelCategory.CONCERT) &&
        hasSpotifyData,
      render: () => (
        <SpotifyCarouselOnHome
          connectUrl={spotifyConnectUrl}
          isSpotifyConnected={isSpotifyConnected}
          options={spotifyOptions}
          size={carouselSize}
        />
      ),
    },
    {
      carouselId: HOME_CAROUSEL_IDS.spotifyConnectBanner,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.EVENT,
      shouldRender: true,
      render: () => (
        <BannersContainer>
          {/* Promo banner (CRH-761, 2a) — RENDER participation, 2 variants (BoM / ODC). Control falls
               through to NGS/Spotify. The control arm and the non-eligible fallback are intentionally
               duplicated — any change to banner ordering must be applied to both paths. */}
          {isPromoBannerPending ? (
            <PromoBannerSkeleton />
          ) : isPromoBannerEligible ? (
            <MultiVariateFeature
              id={FeatureSetEnum.TPromoBannerATG2a}
              recordType={RecordParticipationTypes.RENDER}
              control={() =>
                showNgsSeasonTicketsBanner ? (
                  <HomeSeasonTicketsAppPromo />
                ) : (
                  showSpotifyBanner && (
                    <ConnectToSpotifyBanner
                      connectUrl={connectToSpotify?.connectUrl}
                      isConnected={showSpotifyConnectedState}
                      importedArtistCount={showSpotifyConnectedState ? connectToSpotify?.importedArtistCount : 0}
                      isCompact={false}
                      experimentVariant={0}
                    />
                  )
                )
              }
              variants={[
                () => (
                  <Suspense fallback={null}>
                    <LazyPromoBanner banner="bom" variant={1} />
                  </Suspense>
                ),
                () => (
                  <Suspense fallback={null}>
                    <LazyPromoBanner banner="odc" variant={2} />
                  </Suspense>
                ),
              ]}
            />
          ) : showNgsSeasonTicketsBanner ? (
            <HomeSeasonTicketsAppPromo />
          ) : (
            showSpotifyBanner && (
              <ConnectToSpotifyBanner
                connectUrl={connectToSpotify?.connectUrl}
                isConnected={showSpotifyConnectedState}
                importedArtistCount={showSpotifyConnectedState ? connectToSpotify?.importedArtistCount : 0}
                isCompact={false}
              />
            )
          )}
          {hasChaseSapphireBanner && (
            <SapphireRewardsBanner learnMoreUrl={model.sapphireRewardsBanner?.learnMoreUrl ?? undefined} />
          )}
        </BannersContainer>
      ),
    },
    // trending events
    {
      carouselId: HOME_CAROUSEL_IDS.trendingEvents,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.EVENT,
      shouldRender: showTrendingEvents,
      render: () =>
        wrapCarousel(
          () => <TrendingEventsInLocaleOnHome selectedCarouselGenre={selectedCarouselGenre} size={carouselSize} />,
          eventSkeleton,
        ),
    },
    // coming up for you — previously purchased performers/teams with upcoming local events
    // (exp 103527 / DMS-225). Slot only included for variant users WITH eligible items so
    // control users do not occupy a slot in the carousel sequence — that would shift
    // `entityPosition` for every carousel below this one, corrupting cross-arm telemetry.
    {
      carouselId: HOME_CAROUSEL_IDS.comingUpForYou,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
      shouldRender: comingUpForYouVariant >= 1 && (comingUpForYouItems?.length ?? 0) > 0,
      render: () =>
        wrapCarousel(() => (
          <ComingUpForYouOnHome
            items={comingUpForYouItems ?? EMPTY_COMING_UP_FOR_YOU_ITEMS}
            carouselSize={carouselSize}
          />
        )),
    },
    {
      carouselId: HOME_CAROUSEL_IDS.worldCup,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
      shouldRender: showWorldCupHomepageWidget,
      render: () => (
        <WorldCupWidget carouselId={HOME_CAROUSEL_IDS.worldCup} categoryUrl={model.worldCupCategoryUrl ?? undefined} />
      ),
    },
    // your favorite team — team-branded matchup cards for the user's favorite team (exp 103970).
    // Slot only included when the user has a favorite team WITH upcoming events, per the same
    // entityPosition-stability reasoning as Coming Up For You above.
    {
      carouselId: HOME_CAROUSEL_IDS.favoriteTeam,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.EVENT,
      shouldRender:
        showFavoriteTeamCarousel ||
        (isAssignedFavoriteTeamVariant && isFavoriteTeamEligibleUser && favoriteTeam.isLoading),
      render: () =>
        showFavoriteTeamCarousel ? (
          wrapCarousel(() => (
            <FavoriteTeamCarousel
              carouselId={HOME_CAROUSEL_IDS.favoriteTeam}
              favoriteTeamCategoryId={favoriteTeam.categoryId as number}
              events={favoriteTeam.events}
              categoryUrl={favoriteTeam.url}
            />
          ))
        ) : (
          <CarouselSkeleton />
        ),
    },
    // because you browsed — personalized categories based on recent browsing (exp 103386).
    {
      carouselId: HOME_CAROUSEL_IDS.becauseYouBrowsed,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
      shouldRender: showBecauseYouBrowsed,
      render: () =>
        wrapCarousel(() => (
          <BecauseYouBrowsedOnHome selectedCarouselGenre={selectedCarouselGenre} carouselSize={carouselSize} />
        )),
    },
    // recommended for you - can be either events or categories
    {
      carouselId: HOME_CAROUSEL_IDS.recommendedForYou,
      kind: showRecommendedEvents ? SEARCH_DISCOVERY_ENTITY_TYPES.EVENT : SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
      shouldRender: true,
      render: () =>
        wrapCarousel(
          () =>
            showRecommendedEvents ? (
              <GenericEventCarousel
                carouselId={HOME_CAROUSEL_IDS.recommendedForYou}
                carouselKey={HOME_CAROUSEL_KEYS.HOME_RECOMMENDED_FOR_YOU_EVENTS}
                carouselData={model.recommendationEvents}
                isLoading={false}
                title={<Content id="cid$#3145970#" />}
                carouselLocation={PAGE_IDS.HOME}
                size={carouselSize}
                refreshDependencies={[dates ?? null]}
                shouldRefreshCarousel
                analyticsCarouselId={CarouselId.RecommendedForYou}
              />
            ) : (
              <RecommendedForYouOnHome
                selectedCarouselGenre={selectedCarouselGenre}
                items={model.recommendationCategories.categoryItems ?? {}}
                carouselSize={carouselSize}
                title={<Content id="cid$#3145970#" />}
              />
            ),
          showRecommendedEvents ? eventSkeleton : undefined,
        ),
    },
    // saved events
    {
      carouselId: HOME_CAROUSEL_IDS.savedEvents,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.EVENT,
      shouldRender: showSavedEventsCarousel,
      render: () =>
        wrapCarousel(
          () => (
            <SavedEventsOnHome size={carouselSize} initialEvents={savedEventsData} itemsRevision={savedEventsSyncKey} />
          ),
          eventSkeleton,
        ),
    },
    // top subcategories
    {
      carouselId: HOME_CAROUSEL_IDS.topSubcategories,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
      shouldRender: selectedCarouselGenre === TopLevelCategory.ALL && !isInRemovePopularCategoriesExperiment,
      render: () => wrapCarousel(() => <TopSubcategoriesOnHome size={carouselSize} />),
    },
    // don't miss events
    {
      carouselId: HOME_CAROUSEL_IDS.dontMissEvents,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.EVENT,
      shouldRender: true,
      render: () =>
        wrapCarousel(
          () => <DontMissEvents variant={2} topLevelCategoryId={selectedCarouselGenre} size={carouselSize} />,
          eventSkeleton,
        ),
    },
    // recently viewed
    {
      carouselId: HOME_CAROUSEL_IDS.recentlyViewed,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
      shouldRender: showRecentlyViewed && !isFilterFocusMode,
      render: () => wrapCarousel(() => <RecentlyViewedOnHome size={carouselSize} modelItems={recentlyViewedModel} />),
    },
    // upcoming based on your favorites
    {
      carouselId: HOME_CAROUSEL_IDS.upcomingEvents,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.EVENT,
      shouldRender: upcomingEventsHasData,
      render: () =>
        wrapCarousel(
          () => (
            <UpcomingEventsBasedOnYourFavoritesHomeSection
              recommendedBasedOnFavoriteEvents={model.recommendedBasedOnFavoriteEvents}
              renderView={responsiveCarouselRenderFunction({
                carouselId: HOME_CAROUSEL_IDS.upcomingEvents,
              })}
              size={carouselSize}
            />
          ),
          eventSkeleton,
        ),
    },
    // more from genres you like — personalized concert events ranked by the user's MusicBrainz
    // genre affinity. Marquee treatment for exp 102911; standard-carousel treatment with a
    // variant-driven title for the relaunch experiment (DiscoveryWebMbMusicYouLikeV2).
    {
      carouselId: HOME_CAROUSEL_IDS.mbMusicYouLike,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.EVENT,
      // Slot only included once the registry-level fetch confirms items, so empty/pending
      // states never occupy a carousel position (see comment on useMoreFromGenresYouLikeQuery).
      shouldRender: showMbMusicYouLikeSlot && (mbMusicYouLikeData?.items?.length ?? 0) > 0,
      render: () =>
        wrapCarousel(
          () =>
            mbMusicYouLikeV2Variant > 0 ? (
              <MoreFromGenresYouLike
                display="standard"
                titleMode={mbMusicYouLikeV2Variant >= 2 ? "becauseYouLike" : "plain"}
                data={mbMusicYouLikeData}
              />
            ) : (
              <MoreFromGenresYouLike display="marquee" titleMode="genreSuffix" data={mbMusicYouLikeData} />
            ),
          eventSkeleton,
        ),
    },
    // concerts
    {
      carouselId: HOME_CAROUSEL_IDS.concerts,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
      shouldRender:
        selectedCarouselGenre === TopLevelCategory.CONCERT || selectedCarouselGenre === TopLevelCategory.ALL,
      render: () =>
        wrapCarousel(() => (
          <ContentLayoutContainer as="div">
            <GenericCategoryGridWrapper
              categoryId={TopLevelCategory.CONCERT}
              carouselName={HOME_EVENT_NAMES.CONCERTS_CAROUSEL}
              data={model.concerts}
              dedupeCategoryIds={dedupeCategoryIds}
              removeMargin
              renderView={responsiveCarouselRenderFunction({
                carouselId: HOME_CAROUSEL_IDS.concerts,
                analyticsCarouselId: CarouselId.Concerts,
                analyticsItemType: CarouselItemType.Category,
              })}
              text={concertsText}
              exploreText={concertsText}
              recommendationsPageEvent={recommendationsPageEvent}
              textOnCard
              size={carouselSize}
            />
          </ContentLayoutContainer>
        )),
    },
    // sports
    {
      carouselId: HOME_CAROUSEL_IDS.sports,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
      shouldRender: selectedCarouselGenre === TopLevelCategory.SPORT || selectedCarouselGenre === TopLevelCategory.ALL,
      render: () =>
        wrapCarousel(() => (
          <ContentLayoutContainer as="div">
            <GenericCategoryGridWrapper
              categoryId={TopLevelCategory.SPORT}
              carouselName={HOME_EVENT_NAMES.SPORTS_CAROUSEL}
              data={model.sports}
              dedupeCategoryIds={dedupeCategoryIds}
              removeMargin
              renderView={responsiveCarouselRenderFunction({
                carouselId: HOME_CAROUSEL_IDS.sports,
                analyticsCarouselId: CarouselId.Sports,
                analyticsItemType: CarouselItemType.Category,
              })}
              text={sportsText}
              exploreText={sportsText}
              recommendationsPageEvent={recommendationsPageEvent}
              textOnCard
              size={carouselSize}
            />
          </ContentLayoutContainer>
        )),
    },
    // theatre
    {
      carouselId: HOME_CAROUSEL_IDS.theatre,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
      shouldRender:
        selectedCarouselGenre === TopLevelCategory.THEATER || selectedCarouselGenre === TopLevelCategory.ALL,
      render: () =>
        wrapCarousel(() => (
          <ContentLayoutContainer as="div">
            <GenericCategoryGridWrapper
              categoryId={TopLevelCategory.THEATER}
              carouselName={HOME_EVENT_NAMES.THEATRE_CAROUSEL}
              data={model.theater}
              dedupeCategoryIds={dedupeCategoryIds}
              removeMargin
              renderView={responsiveCarouselRenderFunction({
                carouselId: HOME_CAROUSEL_IDS.theatre,
                analyticsCarouselId: CarouselId.Theatre,
                analyticsItemType: CarouselItemType.Category,
              })}
              text={theatreText}
              exploreText={theatreText}
              recommendationsPageEvent={recommendationsPageEvent}
              textOnCard
              size={carouselSize}
            />
          </ContentLayoutContainer>
        )),
    },
    // email opt-in banner / popup trigger
    {
      carouselId: HOME_CAROUSEL_IDS.emailOptInBanner,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY, // Banner doesn't track entities, using category as default
      shouldRender: shouldShowEmailOptInBanner,
      render: () =>
        wrapCarousel(() =>
          emailOptInPopupTrigger ? (
            <InViewTracker onInView={emailOptInPopupTrigger} />
          ) : (
            <EmailOptInBanner
              email={emailOptInEmail ?? undefined}
              privacyPolicyMessage={model.privacyPolicyMessage}
              onEmailOptIn={handleEmailOptIn}
              onInView={onEmailOptInSlotInView}
            />
          ),
        ),
    },
    // comedy
    {
      carouselId: HOME_CAROUSEL_IDS.comedy,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
      shouldRender:
        selectedCarouselGenre === TopLevelCategory.THEATER || selectedCarouselGenre === TopLevelCategory.ALL,
      render: () =>
        wrapCarousel(() => (
          <ContentLayoutContainer as="div">
            <GenericCategoryGridWrapper
              categoryId={TopLevelCategory.COMEDY}
              carouselName={HOME_EVENT_NAMES.COMEDY_CAROUSEL}
              data={model.comedy}
              dedupeCategoryIds={dedupeCategoryIds}
              removeMargin
              renderView={responsiveCarouselRenderFunction({
                carouselId: HOME_CAROUSEL_IDS.comedy,
                analyticsCarouselId: CarouselId.Comedy,
                analyticsItemType: CarouselItemType.Category,
              })}
              text={comedyText}
              exploreText={comedyText}
              recommendationsPageEvent={recommendationsPageEvent}
              textOnCard
              size={carouselSize}
            />
          </ContentLayoutContainer>
        )),
    },
  ];

  // Exp 104039 (DMS-557): server only stamps continueBrowsingBanner for StubHub mobile-web
  // banner variants when a banner-eligible event exists. Gate on the payload alone — a viewport
  // check would drop it for mobile-web UAs at/above the tablet breakpoint (e.g. landscape phones).
  const continueBrowsingBanner = recentlyViewedModel?.continueBrowsingBanner ?? null;
  const cwyloCarousel = recentlyViewedModel?.cwyloCarousel ?? null;

  // Apply sorting logic internally
  const sortedComponents = useCarouselSorting({
    carousels,
    sortOrder: model.carouselSortOrder,
    isMobile,
    moveUpRecentlyViewedCarousel,
    selectedCarouselGenre,
    isStubhub,
    hasSpotifyData,
    carouselSize,
    showRecentlyViewedAboveHeroBanner,
    showContinueBrowsingBanner: !!continueBrowsingBanner,
    continueBrowsingBanner,
    showContinueWhereYouLeftOff: !!cwyloCarousel && showRecentlyViewed && !isFilterFocusMode,
    continueWhereYouLeftOffCarousel: cwyloCarousel,
    ...(connectToSpotify?.connectUrl && { spotifyConnectUrl: connectToSpotify?.connectUrl }),
    ...(connectToSpotify?.isSpotifyConnected && {
      isSpotifyConnected: connectToSpotify?.isSpotifyConnected,
    }),
    ...(connectToSpotify?.options && { spotifyOptions: connectToSpotify?.options }),
  });

  return {
    sortedComponents,
  };
};
